import { Link } from 'react-router-dom'
import { Lock, CheckCircle2 } from 'lucide-react'
import type { ChapterStatus } from '@/types/storytelling'

interface ChapterCardProps {
  index: number
  id: string
  title: string
  hook: string
  payoff: string
  status: ChapterStatus
}

export function ChapterCard({ index, id, title, hook, payoff, status }: ChapterCardProps) {
  const chapterNumber = index + 1

  if (status === 'locked') {
    return (
      <div className="flex gap-3 items-start opacity-50">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0" style={{ background: 'var(--color-story-locked)' }}>
          <Lock className="w-4 h-4" />
        </div>
        <div className="flex-1 rounded-xl p-4" style={{ background: 'var(--color-story-bg-light)', border: '1px solid var(--color-story-border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-story-primary-light)' }}>Chapter {chapterNumber}</p>
          <p className="font-semibold text-sm mt-0.5" style={{ color: 'var(--color-story-text)' }}>{title}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--color-story-text-muted)' }}>{hook}</p>
        </div>
      </div>
    )
  }

  if (status === 'completed') {
    return (
      <div className="flex gap-3 items-start">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0" style={{ background: 'var(--color-story-primary)' }}>
          <CheckCircle2 className="w-4 h-4" />
        </div>
        <Link to={`/storytelling/lesson/${id}`} className="flex-1 rounded-xl p-4 bg-white transition-shadow hover:shadow-md" style={{ border: '1px solid var(--color-story-border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-story-primary-light)' }}>Chapter {chapterNumber}</p>
          <p className="font-semibold text-sm mt-0.5" style={{ color: 'var(--color-story-text)' }}>{title}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--color-story-text-muted)' }}>{hook}</p>
          <span className="inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded-md" style={{ background: 'var(--color-story-bg)', color: 'var(--color-story-primary)' }}>Completed</span>
        </Link>
      </div>
    )
  }

  // Active
  return (
    <div className="flex gap-3 items-start">
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: 'var(--color-story-accent)' }}>{chapterNumber}</div>
      <Link to={`/storytelling/lesson/${id}`} className="flex-1 rounded-xl p-4 bg-white" style={{ border: '2px solid var(--color-story-accent)', boxShadow: '0 2px 8px rgba(232,168,56,0.15)' }}>
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-story-accent)' }}>Chapter {chapterNumber} — Up Next</p>
        <p className="font-semibold text-sm mt-0.5" style={{ color: 'var(--color-story-text)' }}>{title}</p>
        <p className="text-xs mt-0.5" style={{ color: 'var(--color-story-text-muted)' }}>{hook}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--color-story-text-muted)' }}>{payoff}</p>
        <div className="mt-3 text-center py-2 rounded-lg text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, var(--color-story-primary), var(--color-story-primary-light))' }}>Continue Your Journey</div>
      </Link>
    </div>
  )
}
